/**
 * Plan2027Page — /2027-plan.
 *
 * quick-260928-lh8 (John, 2026-09-28): the page IS the editable three-year
 * model. The opening narrative, the Floor / Target / Stretch scenario cards,
 * the attribution table and the historical-basis block were removed — they
 * confused the first read and repeated what the model's inputs already show
 * per box. The API still computes the scenarios (other consumers); this page
 * just does not render them. The model's target box opens on the preliminary
 * 2027 new-logo target (PLAN2027_NEW_LOGO_TARGET, John 2026-10-07), not the
 * Target scenario's bookings.
 *
 * Layout: header → the model (presets → New-logo inputs → Pipeline in hand →
 * Expansion inputs → Results) → Metric trends (quick-261013-hqb: the former
 * /metric-trends tab, now section 5, anchored so each input links to its chart).
 */
import { PLAN2027_NEW_LOGO_TARGET } from '@saledash/shared/plan2027/model3y';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { AppShell } from '../components/AppShell.js';
import { FreshnessPill } from '../components/FreshnessPill.js';
import { MetricTrendsSection } from '../components/plan2027/MetricTrends.js';
import { ThreeYearModel } from '../components/plan2027/ThreeYearModel.js';
import { CacheNotReadyError } from '../lib/api-client.js';
import { usePlan2027 } from '../queries/usePlan2027.js';

import type { FreshnessPill as FreshnessPillData } from '@saledash/shared';

function toFreshnessPill(
  freshness: { lastRefreshedAt: string; tier: 'fresh' | 'aging' | 'stale' } | undefined,
): FreshnessPillData {
  if (!freshness) return { status: 'live', lastRefreshedAt: new Date().toISOString() };
  const refreshedMs = Date.parse(freshness.lastRefreshedAt);
  const ageSeconds = Number.isNaN(refreshedMs)
    ? 0
    : Math.max(0, Math.floor((Date.now() - refreshedMs) / 1000));
  return { status: 'cached', lastRefreshedAt: freshness.lastRefreshedAt, ageSeconds, tier: freshness.tier };
}

function Banner({ tone, children }: { tone: 'warn' | 'bad'; children: React.ReactNode }): React.JSX.Element {
  const cls = tone === 'warn' ? 'bg-warn-bg text-warn-text' : 'bg-bad-bg text-bad-text';
  return <div className={`rounded-[14px] border border-ink-100 p-4 text-sm ${cls}`}>{children}</div>;
}

export function Plan2027Page(): React.JSX.Element {
  const { data, isLoading, isError, error } = usePlan2027();
  const freshness = toFreshnessPill(data?.freshness);

  const versions = data?.versions ?? [];
  const floor = versions.find((v) => v.id === 'floor') ?? versions[0];

  // Plan year is data-driven (falls back to a client guess before data loads).
  const planYear = data?.planYear ?? new Date().getFullYear() + 1;

  useEffect(() => {
    document.title = `${String(planYear)} Plan · Adeptia`;
  }, [planYear]);

  // The Metric Trends area lives at the foot of the model (quick-261013-hqb): a
  // "#metric-trends" / "#trend-<key>" hash (the header link, the "trend ↓" link
  // beside an input, or the old /metric-trends route) scrolls to it once the
  // model has rendered.
  const { hash } = useLocation();
  useEffect(() => {
    if (!data || !hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [data, hash]);

  return (
    <AppShell
      brandSuffix={`${String(planYear)} Plan`}
      crossLink={{ label: 'Main scorecard ↗', to: '/main' }}
      extraLinks={[
        { label: 'Metric trends ↓', to: '/2027-plan#metric-trends' },
        { label: 'Forecast ↗', to: '/forecast' },
      ]}
      freshnessSlot={<FreshnessPill freshness={freshness} />}
    >
      <div className="space-y-5">
        {isLoading && (
          <div className="rounded-[14px] border border-ink-100 bg-white p-4 text-sm text-ink-500">
            Loading the {String(planYear)} plan…
          </div>
        )}
        {isError && error instanceof CacheNotReadyError && (
          <Banner tone="warn">
            Plan baselines are still warming up — the underlying data refreshes on the sync-worker cadence (~7 min).
            This view will populate automatically.
          </Banner>
        )}
        {isError && !(error instanceof CacheNotReadyError) && (
          <Banner tone="bad">
            Couldn&apos;t load the {String(planYear)} plan{error instanceof Error ? `: ${error.message}` : '.'}
          </Banner>
        )}

        {data && floor && (
          <>
            <h1 className="text-xl font-bold tracking-tight text-ink-900">
              {String(planYear)}&ndash;{String(planYear + 2)} Plan &middot; three-year model
            </h1>
            <ThreeYearModel
              baseline={data.baseline}
              cycleTiming={data.cycleTiming}
              // the preliminary 2027 new-logo target (John 2026-10-07), not the scenario model's Target bookings
              defaultTargetNewArr={PLAN2027_NEW_LOGO_TARGET.amount}
              planYear={planYear}
            />
            {/* 5 · Metric trends — the measured history behind the section-1 boxes (quick-261013-hqb) */}
            <MetricTrendsSection />
          </>
        )}
      </div>
    </AppShell>
  );
}
